import { useState } from "react";
import { Form, Button, Row, Col, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import { useCreateClientMutation } from "../../../redux/api/ClientsApi/clientsApi";
import { FaPlus, FaSave, FaArrowLeft, FaImage } from "react-icons/fa";
import "../Services/Services.css";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import { useFormik } from "formik";
import * as Yup from "yup";

const AddClients = () => {
  const navigate = useNavigate();
  const [createClient, { isLoading }] = useCreateClientMutation();
  const [imagePreview, setImagePreview] = useState(null);

  const validationSchema = Yup.object({
    client_name: Yup.string().trim().required("Client name is required"),
    display_order: Yup.number(),
    status: Yup.boolean(),
    client_logo: Yup.mixed().required("Client logo is required"),
  });

  const formik = useFormik({
    initialValues: {
      client_name: "",
      display_order: 0,
      status: true,
      client_logo: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      const data = new FormData();
      Object.keys(values).forEach((key) => {
        data.append(key, values[key]);
      });

      try {
        await createClient(data).unwrap();
        message.success("Client added successfully!");
        setTimeout(() => navigate("/admin/clients"), 2000);
      } catch (error) {
        message.error(error.data?.error || "Failed to add client");
      }
    },
  });

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      formik.setFieldValue("client_logo", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center bg-light"
        style={{ height: "100vh" }}
      >
        <LoaderSpinner />
      </div>
    );
  }

  return (
    <div className="container py-4 fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="fw-bold text-dark mb-1">
            <FaPlus className="text-primary me-2" />
            Add Client
          </h2>
          <p className="text-muted mb-0">Add a new client partner</p>
        </div>
        <Button
          variant="outline-secondary"
          className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-4"
          onClick={() => navigate("/admin/clients")}
        >
          <FaArrowLeft /> Back to List
        </Button>
      </div>

      <Form onSubmit={formik.handleSubmit}>
        <Card className="border-0 overflow-hidden">
          <Card.Body className="p-4 p-md-5">
            <h5 className="mb-4 text-secondary border-start border-4 border-primary ps-3">
              Client Information
            </h5>
            <Row className="g-4">
              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Client Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    name="client_name"
                    {...formik.getFieldProps("client_name")}
                    isInvalid={
                      formik.touched.client_name && !!formik.errors.client_name
                    }
                    placeholder="e.g. Google"
                  />
                  <Form.Control.Feedback type="invalid">
                    {formik.errors.client_name}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="fw-semibold">Display Order</Form.Label>
                  <Form.Control
                    type="number"
                    name="display_order"
                    {...formik.getFieldProps("display_order")}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="fw-semibold">
                    Client Logo <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="custom-file-container">
                    <input
                      type="file"
                      id="client-logo-input"
                      accept="image/*"
                      onChange={handleFileChange}
                      hidden
                    />
                    <label
                      htmlFor="client-logo-input"
                      className={`d-flex align-items-center gap-2 border rounded-3 p-1 cursor-pointer bg-white shadow-sm ${formik.touched.client_logo && formik.errors.client_logo ? "border-danger" : ""}`}
                      style={{ minHeight: "45px" }}
                    >
                      <div className="btn btn-primary btn-sm rounded-pill px-3 ms-1">
                        Choose File
                      </div>
                      <span className="text-muted small text-truncate px-2 flex-grow-1">
                        {formik.values.client_logo
                          ? formik.values.client_logo.name
                          : "No file chosen"}
                      </span>
                      <div className="pe-3 text-primary">
                        <FaImage />
                      </div>
                    </label>
                  </div>
                  {formik.touched.client_logo && formik.errors.client_logo && (
                    <div className="text-danger small mt-1">
                      {formik.errors.client_logo}
                    </div>
                  )}
                  {imagePreview && (
                    <div className="mt-3">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="rounded shadow-sm border p-1"
                        style={{
                          maxWidth: "200px",
                          maxHeight: "100px",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  )}
                </Form.Group>
              </Col>

              <Col md={12} className="d-flex align-items-center">
                <Form.Group>
                  <Form.Check
                    type="switch"
                    id="client-status"
                    label="Active / Published"
                    name="status"
                    checked={formik.values.status}
                    onChange={formik.handleChange}
                    className="fw-bold text-primary"
                  />
                </Form.Group>
              </Col>
            </Row>

            <div className="d-flex justify-content-end gap-3 mt-5">
              <Button
                variant="light"
                className="rounded-pill px-4"
                onClick={() => navigate("/admin/clients")}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                type="submit"
                className="rounded-pill px-5 shadow-sm d-flex align-items-center gap-2"
              >
                <FaSave /> Save Client
              </Button>
            </div>
          </Card.Body>
        </Card>
      </Form>
    </div>
  );
};

export default AddClients;
